import { BiddingUtils } from '@/vb-utils/bidding.util';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import {
    AccountAddress,
    GetAllAddresses200Response,
    ProfilesService,
    Response,
    UsersService,
    AccountPreferenceByAccountMappingsId200Response,
    AccountPreferences,
    PutAccountPreferencesRequest,
} from '@viewbid/ts-angular-client';
import { ActivatedRoute, Router } from '@angular/router';
import { VbSnackbarService } from '@viewbid/ui';
import { UsersErrorCodes } from '../../enums/users-error-codes';

@Component({
    selector: 'vb-user-addresses',
    templateUrl: './user-addresses.component.html',
    styleUrls: ['./user-addresses.component.scss'],
})
export class UserAddressesComponent implements OnInit {
    public userAddresses: Array<AccountAddress> = [];
    public billingAddresses: Array<AccountAddress> = [];
    public shippingAddresses: Array<AccountAddress> = [];
    private successToastId: number = 0;
    userPreferences: any = {};
    defaultAddresses: string = '';
    preferenceId: string = '';
    accountPreferences: AccountPreferences = {};
    isLoading: boolean = false;
    slides: AccountAddress[][] = [];

    /** Remove-address confirm (vb-dialog) — replaces the legacy
     *  ngx-bootstrap ConfirmationDialogComponent for this flow. */
    deleteConfirmOpen: boolean = false;
    private pendingDeleteId: string | null = null;
    constructor(
        private userService: UsersService,
        private biddingUtils: BiddingUtils,
        private snackbarService: VbSnackbarService,
        private translate: TranslateService,
        private profileService: ProfilesService,
        private cdr: ChangeDetectorRef,
        private router: Router,
        protected route: ActivatedRoute,
    ) {}
    ngOnInit(): void {
        this.isLoading = true;
        this.biddingUtils
            .getCurrentUserId()
            .subscribe(async (currentUserId: string) => {
                await this.getUserPreferences(currentUserId);
                await this.getallAddressesByUserId();
            });
    }

    goToAddAddress(): void {
        this.router.navigate(['../user-addresses'], { relativeTo: this.route });
    }

    private async getallAddressesByUserId(): Promise<void> {
        // getting all user addresses by providing page size -1
        this.userService.getAllAddresses(1, -1).subscribe({
            next: (response: GetAllAddresses200Response) => {
                this.userAddresses = response.data;
                this.sortedAddresses(this.userAddresses);
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.snackbarService.error(
                    this.translate.instant('Users.getAddressesError'),
                );
            },
        });
    }

    async updateUserPreferences(id?: string): Promise<void> {
        if (!id) return;
        this.isLoading = true;
        this.userPreferences.defaultAddressId = id;
        const request: PutAccountPreferencesRequest = {
            preferences: this.userPreferences,
            registrationStatus: this.accountPreferences.registrationStatus!,
            spendingCap: this.accountPreferences.spendingCap ?? null,
            accountMappingsId: this.accountPreferences.accountMappingsId!,
        };
        await this.userService
            .patchAccountPreferencesById(this.preferenceId, request)
            .subscribe({
                next: (response: Response) => {
                    this.defaultAddresses =
                        response.data?.['preferences']?.defaultAddressId;
                    this.snackbarService.dismiss(this.successToastId);
                    this.successToastId = this.snackbarService.success(
                        this.translate.instant(
                            'Users.updateUserPreferencesSuccess',
                        ),
                    );
                    this.isLoading = false;
                    this.sortedAddresses(this.userAddresses);
                    this.cdr.detectChanges();
                },
                error: () => {
                    this.isLoading = false;
                    this.snackbarService.error(
                        this.translate.instant(
                            'Users.updateUserPreferencesError',
                        ),
                    );
                },
            });
    }

    isDefaultAddress(addressId?: string): boolean {
        if (!addressId) return false;
        if (this.defaultAddresses) {
            return this.defaultAddresses === addressId;
        } else return false;
    }

    /**
     * Maps an AccountAddress onto vb-address-card display lines:
     * street / city, province / country. Empty parts are dropped; the
     * postal code is passed separately via [postal].
     */
    getAddressLines(address: AccountAddress): string[] {
        const street: string = [address.address1, address.address2]
            .filter((part?: string) => !!part)
            .join(' ');
        const cityProvince: string = [address.city, address.province]
            .filter((part?: string) => !!part)
            .join(', ');
        const country: string =
            address.country === 'CA' ? 'Canada' : (address.country ?? '');
        return [street, cityProvince, country].filter((line: string) => !!line);
    }

    async getUserPreferences(userId: string): Promise<void> {
        if (userId) {
            await this.profileService
                .accountPreferenceByAccountMappingsId(userId)
                .subscribe({
                    next: (
                        response: AccountPreferenceByAccountMappingsId200Response,
                    ) => {
                        const preferenceResponse: AccountPreferences =
                            response.data as AccountPreferences;
                        this.preferenceId = preferenceResponse.id ?? '';
                        this.userPreferences =
                            preferenceResponse.preferences ?? {};
                        this.accountPreferences = preferenceResponse;
                        this.defaultAddresses =
                            preferenceResponse.preferences?.defaultAddressId ??
                            '';
                    },
                    error: () => {
                        this.isLoading = false;
                        this.snackbarService.error(
                            this.translate.instant(
                                'Users.getUserPreferencesError',
                            ),
                        );
                    },
                });
        }
    }

    onDeleteMenuClick(addressId?: string): void {
        if (!addressId) return;
        // Mirror the API rules up front so the user isn't asked to confirm
        // a delete that will be refused.
        if (this.userAddresses.length <= 1) {
            this.snackbarService.error(
                this.translate.instant('Users.deleteLastAddressError'),
            );
            return;
        }
        if (this.isDefaultAddress(addressId)) {
            this.snackbarService.error(
                this.translate.instant('Users.deleteDefaultAddressError'),
            );
            return;
        }
        this.pendingDeleteId = addressId;
        this.deleteConfirmOpen = true;
    }

    /** Same translated copy the legacy confirmation dialog showed. */
    get deleteConfirmTitle(): string {
        return this.translate.instant('Users.deleteAddressConfirmationTitle');
    }

    get deleteConfirmMessage(): string {
        return this.translate.instant('Users.deleteAddressConfirmationMessage');
    }

    closeDeleteConfirm(): void {
        this.deleteConfirmOpen = false;
        this.pendingDeleteId = null;
    }

    onDeleteConfirmed(): void {
        const addressId: string | null = this.pendingDeleteId;
        this.closeDeleteConfirm();
        if (addressId) this.deleteAddress(addressId);
    }

    private async deleteAddress(addressId: string): Promise<void> {
        this.isLoading = true;
        await this.userService.deleteUserAddressById(addressId).subscribe({
            next: () => {
                this.snackbarService.dismiss(this.successToastId);
                this.successToastId = this.snackbarService.success(
                    this.translate.instant('Users.deleteAddressSuccess'),
                );
                this.userAddresses = this.userAddresses.filter(
                    (address: AccountAddress) => address.id !== addressId,
                );
                this.isLoading = false;
            },
            error: (error: HttpErrorResponse) => {
                this.isLoading = false;
                this.snackbarService.error(
                    this.translate.instant(
                        this.deleteAddressErrorKey(error.error?.errorCode),
                    ),
                );
            },
        });
    }

    private deleteAddressErrorKey(errorCode?: number): string {
        switch (errorCode) {
            case UsersErrorCodes.ACCOUNT_ADDRESS_DEFAULT_DELETE:
                return 'Users.deleteDefaultAddressError';
            case UsersErrorCodes.ACCOUNT_ADDRESS_LAST_DELETE:
                return 'Users.deleteLastAddressError';
            default:
                return 'Users.deleteAddressError';
        }
    }

    private sortedAddresses(addresses: AccountAddress[]): AccountAddress[] {
        if (addresses.length === 0) {
            return [];
        }
        return addresses.sort((a: AccountAddress, b: AccountAddress) => {
            return (
                (this.isDefaultAddress(b.id) ? 1 : 0) -
                (this.isDefaultAddress(a.id) ? 1 : 0)
            );
        });
    }

    goToEditAddress(id?: string): void {
        if (!id) return;
        this.router.navigate(['../user-addresses', id], {
            relativeTo: this.route,
        });
    }
}
